import type {ToastMessageService} from 'shared/components/kp-toast-messages';
import type {DepartmentResponse, FieldId, FieldTypeId, Fond, RecordHolding, RecordStatusId, SetFieldValueRequest, UUID} from 'src/typings';
import type {GridStateManager} from 'shared/ui-widgets/grid/state/state-management';
import type {Row, Table} from '@tanstack/svelte-table';
import type {Logger} from 'src/core/logging/types';
import type {GridCommand} from 'shared/ui-widgets/grid/types';
import {CellCoordinates} from 'shared/ui-widgets/grid/types';
import type {ClipboardCellData} from 'shared/ui-widgets/grid/clipboard/clipboard';
import {createClipBoardItem, resolveClipboardData} from 'shared/ui-widgets/grid/clipboard/clipboard';
import {convertToCellCoordinatesMatrix, getCellValueByCoordinates, getColumnByIndex, getRowByIndex, isRectangle} from 'shared/ui-widgets/grid/utils';
import {assertUnreachable, cleanup, exists, getId, isNullOrUndefined} from 'shared/utils/custom-utils';
import type {GridField, GridFieldMetadata, HierarchicalRecordRow, KpRecordGridDisplayEvents, RecordRow, SimpleGridFieldValue, SourceAwareRecordRow} from 'src/features/record-grid/lib/types';
import type {Observable, Subscription} from 'rxjs';
import {BehaviorSubject, combineLatest, EMPTY, firstValueFrom, from, of, Subject} from 'rxjs';
import {catchError, concatMap, filter, map, take, tap} from 'rxjs/operators';
import {byIdOf, distinct, distinctBy, isNotEmpty, matrixMap, removeAll, replaceAll, uuidComparator, zip} from 'shared/utils/array-utils';
import type {RecordGridService} from 'src/features/record-grid/services/record-grid.service';
import type {Readable, Unsubscriber} from 'svelte/store';
import {COLUMN_TYPES} from 'src/features/record-grid/lib/constants';
import {resolveGridFieldAttributeValue} from 'src/features/record-grid/lib/grid-utils';
import type {EventDispatcher} from 'svelte';
import {tick} from 'svelte';
import {deleteFieldByFieldIdRecursive, setFieldByFieldIdRecursive} from 'src/features/record-grid/lib/grid-fields';
import type {LocalizeFunction} from 'core/types';
import type {PlainTextParsingContext} from 'src/features/record-grid/lib/cell-serialization-and-deserialization';
import {marshallCellValue, unmarshallCellValue} from 'src/features/record-grid/lib/cell-serialization-and-deserialization';
import {generateIdOfInitialField} from 'src/features/record-grid/components/cells/data-cell/utils';
import {asFieldTypeId} from 'shared/utils/record-field-utils';
import {bufferWithDebounceTimeAndSignalFlushing, updateValue} from 'shared/utils/observables-utils';
import {isDraft, isSubrow, markRowWithDataSource, removeHierarchyProperties} from 'src/features/record-grid/lib/record-row';
import {isNotEmptyCollection} from 'shared/utils/collection-utils';
import {isEmptyObject} from 'shared/utils/object-utils';

export interface FieldUpdateRequest<T extends SimpleGridFieldValue> extends GridFieldMetadata {
    value: T | null;
    row: Row<HierarchicalRecordRow>;
    regenerate: boolean;
}

type ServerUpdateResult = {
    updates: FieldUpdateRequest<SimpleGridFieldValue>[];
    effectedRecordIds: UUID[];
};

export class RecordGridDataManager {

    private table: Table<HierarchicalRecordRow>;
    private readonly updateQueue$ = new Subject<FieldUpdateRequest<SimpleGridFieldValue>>();
    private readonly updatesSubscription: Subscription;
    private readonly bufferFlushSignal$ = new Subject<void>();
    private readonly dirtyRowsIds$ = new BehaviorSubject<Set<UUID>>(new Set());
    private readonly pendingSyncRowsIdsWithRequestsCount$ = new BehaviorSubject<Map<UUID, number>>(new Map());
    private readonly isProcessingEdits$: Observable<boolean>;
    private readonly commandsSubscription: Subscription;
    private readonly tableUnsubscriber: Unsubscriber;

    constructor(private table$: Readable<Table<HierarchicalRecordRow>>,
                private stateManager: GridStateManager,
                private flatRootRows$: BehaviorSubject<SourceAwareRecordRow[]>,
                private flatDraftRootRows$: BehaviorSubject<SourceAwareRecordRow[]>,
                private flatSubrows$: BehaviorSubject<SourceAwareRecordRow[]>,
                private command$: Subject<GridCommand>,
                private recordGridService: RecordGridService,
                private newRowInitialValues: Record<FieldTypeId, SetFieldValueRequest>,
                private localize: LocalizeFunction,
                private locale: string,
                private logger: Logger,
                private toastMessageService: ToastMessageService,
                private eventDispatcher: EventDispatcher<KpRecordGridDisplayEvents>) {

        this.tableUnsubscriber = this.table$.subscribe((table) => this.table = table);

        this.isProcessingEdits$ = combineLatest([this.dirtyRowsIds$, this.pendingSyncRowsIdsWithRequestsCount$])
            .pipe(map(([dirtyRowsIds, pendingSyncRowsIdsWithRequestsCount]) => isNotEmptyCollection(dirtyRowsIds) || isNotEmptyCollection(pendingSyncRowsIdsWithRequestsCount)));


        const localUpdates$ = this.updateQueue$.pipe(
            tap(({fieldId, value, row}) => this.updateTableDataCellState(row.original, fieldId, value)),
            tap((update) => this.addRowToDirtyRowsTracking(update)),
            bufferWithDebounceTimeAndSignalFlushing(1000, this.bufferFlushSignal$),
            map((updates) => this.keepOnlyLastUpdateForEachRecordRowAndFieldPair(updates)),
            tap((updates) => this.removeRowsFromDirtyRowsTracking(updates))
        );

        const serverUpdates$ = localUpdates$.pipe(
            tap((updates) => this.resolveStartOfPendingSyncRowsTracking(updates)),
            concatMap((updates) => from(this.updateCellValueOnServer(updates)).pipe(catchError(() => of({ updates, effectedRecordIds: [] })))),
            map((result) => this.collectAllAffectedRowsForReload(result.updates, result.effectedRecordIds)),
            map((rows) => distinctBy(rows, getId())),
            concatMap((rows) => from(this.reloadRows(rows)).pipe(catchError(() => EMPTY))),
            tap((rows) => this.resolveCompletionOfPendingSyncRowsTracking(rows)),
            map((rows) => this.filterDirtyOrSyncPendingRows(rows)),
            filter(isNotEmpty),
            tap((rows) => this.refreshRowsInTableData(rows))
        );

        this.updatesSubscription = serverUpdates$.subscribe();

        this.commandsSubscription = this.command$.subscribe((command) => this.processCommand(command));
    }

    public disconnect() {
        cleanup(this.commandsSubscription, this.updatesSubscription, this.tableUnsubscriber);
    }

    public processCommand(command: GridCommand) {
        switch (command.type) {
            case 'delete':
                this.deleteCell(command.target);
                break;
            case 'copy':
                this.copyCell();
                break;
            case 'paste':
                this.pasteCell(command.target);
                break;
            case 'cut':
                this.cutCell(command.target);
                break;
            case 'regenerate':
                this.regenerateCell(command.target);
                break;
            case 'rollback-cell-change':
                break;
            default:
                assertUnreachable(command.type);
        }
    }

    public queueUpdate(update: FieldUpdateRequest<SimpleGridFieldValue>) {
        this.updateQueue$.next(update);
    }

    public getIsProcessingEdits$(): Observable<boolean> {
        return this.isProcessingEdits$;
    }

    public async copyCell() {
        try {
            const state = this.stateManager.getCurrentStateValue();
            if (isRectangle(state.selected.allSelected)) {
                const exportedMatrix = convertToCellCoordinatesMatrix(state.selected.allSelected);
                const cellsData = this.extractCellsData(exportedMatrix);
                const clipboardItem = createClipBoardItem(cellsData);
                await navigator.clipboard.write([clipboardItem]);
                return;
            }

            if (exists(state.cursor.physicalCursor)) {
                const cellData = this.extractCellData(state.cursor.physicalCursor);
                const clipboardItem = createClipBoardItem([[cellData]]);
                await navigator.clipboard.write([clipboardItem]);
                return;
            }
        } catch (error) {
            this.logger.error('Error during copying from grid', error);
            this.toastMessageService.showError('Error during copying from grid');
        }
    }

    public async pasteCell(targetCell: CellCoordinates) {
        try {
            const clipboardItems = await navigator.clipboard.read();
            const clipboardData = await resolveClipboardData(clipboardItems);
            if (exists(clipboardData)) {
                const targetColumn = getColumnByIndex(this.table, targetCell.columnIndex);
                const ctx: PlainTextParsingContext = {
                    valueDatatype: targetColumn.columnDef.meta.valueDatatype,
                    editorType: targetColumn.columnDef.meta.editorType,
                    localize: this.localize,
                    locale: this.locale
                };
                const fieldValues = unmarshallCellValue(clipboardData.at(0).at(0), ctx);
                const row = getRowByIndex(this.table, targetCell.rowIndex);
                const fieldId = generateIdOfInitialField(asFieldTypeId(targetColumn.id));

                this.updateQueue$.next({row, fieldId, value: fieldValues.at(0), regenerate: false});
                this.flushEditsBuffer();
            }
        } catch (error) {
            this.logger.error('Error during pasting values to grid', error);
            this.toastMessageService.showError('Error during pasting values to grid');
        }
    }

    public async cutCell(sourceCell: CellCoordinates) {
        await this.copyCell();
        this.deleteCell(sourceCell);
    }

    public deleteCell(targetCell: CellCoordinates) {
        const row = getRowByIndex(this.table, targetCell.rowIndex);
        const fields = getCellValueByCoordinates<HierarchicalRecordRow, GridField<SimpleGridFieldValue>[]>(this.table, targetCell);

        fields.forEach((field) => this.updateQueue$.next({row, fieldId: field.fieldId, value: null, regenerate: false}));
        this.flushEditsBuffer();
    }

    public regenerateCell(targetCell: CellCoordinates) {
        const row = getRowByIndex(this.table, targetCell.rowIndex);
        const fields = getCellValueByCoordinates<HierarchicalRecordRow, GridField<SimpleGridFieldValue>[]>(this.table, targetCell);

        fields.forEach((field) => this.updateQueue$.next({row, fieldId: field.fieldId, value: null, regenerate: true}));
        this.flushEditsBuffer();
    }

    public async createRow(selectedSubFond: Fond) {
        const initialValues = isNullOrUndefined(this.newRowInitialValues) || isEmptyObject(this.newRowInitialValues) ? undefined : this.newRowInitialValues;
        const newRecordRow = await this.recordGridService.createNewRow(selectedSubFond, initialValues);
        await this.addDraftRow(newRecordRow);
    }

    public async createSubRow(selectedSubFond: Fond, fieldTypeId: FieldTypeId, row: HierarchicalRecordRow) {
        const parentRowReferenceAsInitialValues: Record<FieldTypeId, SetFieldValueRequest> = {[fieldTypeId]: {recordId: row.id}};
        const newRecordRow = await this.recordGridService.createNewRow(selectedSubFond, parentRowReferenceAsInitialValues);
        await this.addDraftSubrow(newRecordRow, row.id);
    }

    public async changeStatus(row: Row<HierarchicalRecordRow>, recordStatusId: RecordStatusId) {
        this.flushEditsBuffer();
        await this.waitUntilEditsProcessingFinishes();
        const {newRow, modifiedRecordIds} = await this.recordGridService.changeStatus(row.original, recordStatusId);
        if (isSubrow(row.original)) {
            this.replaceRowIn(markRowWithDataSource(newRow, 'subrow', row.original.parentRecordRowId), this.flatSubrows$);
        } else if (isDraft(row.original)) {
            this.removeRowFrom(row.original, this.flatDraftRootRows$);
            this.appendRowTo(markRowWithDataSource(newRow, 'root', null), this.flatRootRows$);
        } else {
            this.replaceRowIn(markRowWithDataSource(newRow, 'root', null), this.flatRootRows$);
        }

        const rowsToReload = this.modifiedRecordToExistingRows(modifiedRecordIds);
        if (isNotEmpty(rowsToReload)) {
            const reloadedRows = await this.reloadRows(rowsToReload);
            this.refreshRowsInTableData(reloadedRows)
        }


        this.eventDispatcher('row-published', newRow);
    }

    public async deleteRow(row: Row<HierarchicalRecordRow>) {
        this.flushEditsBuffer();
        await this.waitUntilEditsProcessingFinishes();
        this.removeRowFrom(row.original, this.getOriginalRecordRowSource(row.original));
        await this.recordGridService.deleteRow(row.original);
        this.eventDispatcher('row-deleted', row.original);
    }

    public async duplicateRow(row: Row<HierarchicalRecordRow>) {
        this.flushEditsBuffer();
        await this.waitUntilEditsProcessingFinishes();
        const newRecordRow = await this.recordGridService.duplicateRow(row.original);
        await this.addDraftRow(newRecordRow);
    }

    public async createRecordHolding(recordRow: HierarchicalRecordRow, department: DepartmentResponse) {
        this.flushEditsBuffer();
        await this.waitUntilEditsProcessingFinishes();
        await this.recordGridService.createRecordHolding(recordRow, department);
        const updatedRow = await this.recordGridService.loadSingleRow(recordRow.id);
        this.replaceRowIn(markRowWithDataSource(updatedRow, recordRow.dataSource, recordRow.parentRecordRowId), this.getOriginalRecordRowSource(recordRow));
    }

    public async deleteRecordHolding(recordRow: HierarchicalRecordRow, recordHolding: RecordHolding) {
        this.flushEditsBuffer();
        await this.waitUntilEditsProcessingFinishes();
        await this.recordGridService.deleteRecordHolding(recordHolding);
        const updatedRow = await this.recordGridService.loadSingleRow(recordRow.id);
        this.replaceRowIn(markRowWithDataSource(updatedRow, recordRow.dataSource, recordRow.parentRecordRowId), this.getOriginalRecordRowSource(recordRow));
    }

    private updateTableDataCellState(row: HierarchicalRecordRow, fieldId: FieldId, value: SimpleGridFieldValue) {
        if (exists(value)) {
            this.editCellValueInTableData(row, fieldId, {...value, fieldId});
        } else {
            this.deleteCellValueFromTableData(row, fieldId);
        }
    }

    private async addDraftRow(newRecordRow: RecordRow) {
        this.appendRowTo(markRowWithDataSource(newRecordRow, 'draft', null), this.flatDraftRootRows$);
        await tick();
        this.startEditingInFirstRequiredColumn(newRecordRow);
    }

    private async addDraftSubrow(newRecordRow: RecordRow, parentRecordRowId: UUID) {
        this.appendRowTo(markRowWithDataSource(newRecordRow, 'subrow', parentRecordRowId), this.flatSubrows$);
        await tick();
        this.startEditingInFirstRequiredColumn(newRecordRow);
    }

    private startEditingInFirstRequiredColumn(newRecordRow: RecordRow) {
        const requiredColumn = this.findFirstRequiredColumn(this.table, newRecordRow.fond.id);
        if (isNullOrUndefined(requiredColumn)) {
            return;
        }
        const columnIndex = requiredColumn.getIndex(requiredColumn.getIsPinned() || undefined);
        const rowIndex = this.table.getRowModel().flatRows.findIndex((row) => row.original.id === newRecordRow.id);
        const targetCoordinates = new CellCoordinates({rowIndex, columnIndex});
        this.stateManager.forceEditModeIn(targetCoordinates);
    }

    private editCellValueInTableData(row: HierarchicalRecordRow, fieldId: FieldId, value: GridField) {
        const updatedRow = structuredClone(removeHierarchyProperties(row));
        setFieldByFieldIdRecursive(updatedRow, fieldId, value);
        this.replaceRowIn(updatedRow, this.getOriginalRecordRowSource(row));

    }

    private deleteCellValueFromTableData(row: HierarchicalRecordRow, fieldId: FieldId) {
        const updatedRow = structuredClone(removeHierarchyProperties(row));
        deleteFieldByFieldIdRecursive(updatedRow, fieldId);
        this.replaceRowIn(updatedRow, this.getOriginalRecordRowSource(row));
    }

    private modifiedRecordToExistingRows(modifiedRecordIds) {
        const existingRows = [];

        modifiedRecordIds.forEach((id) => {
            const rowIndex = this.table.getRowModel().flatRows.findIndex(
                (tableRow) => tableRow.original.id === id
            );

            if (rowIndex === -1) {
                return;
            }

            const row = getRowByIndex(this.table, rowIndex);
            if (row) {
                existingRows.push(row);
            }
        });

        return existingRows;
    }

    private async updateCellValueOnServer(updates: FieldUpdateRequest<SimpleGridFieldValue>[]): Promise<ServerUpdateResult> {
        const editUpdates = updates.filter((update) => exists(update.value));
        const deleteUpdates = updates.filter((update) => !exists(update.value) && !update.regenerate);
        const regenerateUpdates = updates.filter((update) => update.regenerate);

        const effectedRecordIds: UUID[] = [];

        if (isNotEmpty(editUpdates)) {
            const response = await this.recordGridService.updateCells(editUpdates.map((update) => ({...update, row: update.row.original})));
            effectedRecordIds.push(...(response.modifiedRecordIds ?? []));
        }
        if (isNotEmpty(deleteUpdates)) {
            const response =  await this.recordGridService.deleteCells(deleteUpdates.map((update) => ({...update, row: update.row.original})));
            effectedRecordIds.push(...(response.modifiedRecordIds ?? []));
        }
        if (isNotEmpty(regenerateUpdates)) {
            const response =  await this.recordGridService.regenerateCells(regenerateUpdates.map((update) => ({...update, row: update.row.original})));
            effectedRecordIds.push(...(response.modifiedRecordIds ?? []));
        }
        return {
            updates,
            effectedRecordIds: distinctBy(effectedRecordIds, (x) => x),
        };
    }

    private extractCellData(coordinates: CellCoordinates): ClipboardCellData {
        const data = getCellValueByCoordinates<HierarchicalRecordRow, GridField<SimpleGridFieldValue>[]>(this.table, coordinates);
        return marshallCellValue(data);
    }

    private extractCellsData(coordinates: CellCoordinates[][]): ClipboardCellData[][] {
        return matrixMap(coordinates, (coordinate) => this.extractCellData(coordinate));
    }

    private addRowToDirtyRowsTracking(update: FieldUpdateRequest<SimpleGridFieldValue>) {
        updateValue(this.dirtyRowsIds$, (dirtyRowsIds) => dirtyRowsIds.add(update.row.original.id));
    }

    private removeRowsFromDirtyRowsTracking(updates: FieldUpdateRequest<SimpleGridFieldValue>[]) {
        const rowsIds = updates.map((update) => update.row.original.id);
        const distinctRecordRowsIds = distinct(rowsIds);

        distinctRecordRowsIds.forEach((recordRowId) => {
            updateValue(this.dirtyRowsIds$, (dirtyRowsIds) => {
                dirtyRowsIds.delete(recordRowId);
                return dirtyRowsIds;
            });
        });
    }

    private resolveStartOfPendingSyncRowsTracking(updates: FieldUpdateRequest<SimpleGridFieldValue>[]) {
        const rowsIds = updates.map((update) => update.row.original.id);
        const distinctRecordRowsIds = distinct(rowsIds);

        distinctRecordRowsIds.forEach((recordRowId) => {
            updateValue(this.pendingSyncRowsIdsWithRequestsCount$, (pendingSyncRowsIdsToRequestCnt) => {
                if (pendingSyncRowsIdsToRequestCnt.has(recordRowId)) {
                    pendingSyncRowsIdsToRequestCnt.set(recordRowId, pendingSyncRowsIdsToRequestCnt.get(recordRowId) + 1);
                } else {
                    pendingSyncRowsIdsToRequestCnt.set(recordRowId, 1);
                }
                return pendingSyncRowsIdsToRequestCnt;
            });
        });
    }

    private resolveCompletionOfPendingSyncRowsTracking(reloadedRows: SourceAwareRecordRow[]) {
        reloadedRows.forEach((row) => {
            const recordRowId = row.id;

            updateValue(this.pendingSyncRowsIdsWithRequestsCount$, (pendingSyncRowsIdsToRequestCnt) => {
                const pendingRequests = pendingSyncRowsIdsToRequestCnt.get(recordRowId);
                if (pendingRequests > 1) {
                    pendingSyncRowsIdsToRequestCnt.set(recordRowId, pendingRequests - 1);
                } else {
                    pendingSyncRowsIdsToRequestCnt.delete(recordRowId);
                }
                return pendingSyncRowsIdsToRequestCnt;
            });
        });
    }

    private keepOnlyLastUpdateForEachRecordRowAndFieldPair(updates: FieldUpdateRequest<SimpleGridFieldValue>[]) {
        return distinctBy(updates, ({row, fieldId}) => `${row.original.id}:${fieldId}`)
    }

    private filterDirtyOrSyncPendingRows(reloadedRows: SourceAwareRecordRow[]) {
        return reloadedRows.filter((row) => {
            return !this.dirtyRowsIds$.getValue().has(row.id) && !this.pendingSyncRowsIdsWithRequestsCount$.getValue().has(row.id)
        });
    }

    private collectAllAffectedRowsForReload(updates: FieldUpdateRequest<SimpleGridFieldValue>[],  effectedRecordIds: UUID[]): HierarchicalRecordRow[] {
        const rows = [...updates.map((update) => update.row), ...this.modifiedRecordToExistingRows(effectedRecordIds)];

        const parentChainsRows = rows.flatMap((row) => row.getParentRows());
        const subtreesRows = rows.flatMap((row) => row.getLeafRows());
        return [...rows, ...parentChainsRows, ...subtreesRows].map((row) => row.original);
    }

    private async reloadRows(originalRows: HierarchicalRecordRow[]): Promise<SourceAwareRecordRow[]> {
        const reloadedRows: RecordRow[] = await this.recordGridService.loadRows(originalRows);
        return zip(originalRows.toSorted(uuidComparator), reloadedRows.toSorted(uuidComparator))
            .map(([originalRow, reloadedRow]) => markRowWithDataSource(reloadedRow, originalRow.dataSource, originalRow.parentRecordRowId));
    }

    private refreshRowsInTableData(reloadedRows: SourceAwareRecordRow[]) {
        const refreshedSubrows = reloadedRows.filter((row) => isSubrow(row));
        const refreshedDraftRootRows = reloadedRows.filter((row) => !isSubrow(row) && isDraft(row));
        const refreshedRootRows = reloadedRows.filter((row) => !isSubrow(row) && !isDraft(row));

        this.replaceRowsIn(refreshedSubrows, this.flatSubrows$);
        this.replaceRowsIn(refreshedDraftRootRows, this.flatDraftRootRows$);
        this.replaceRowsIn(refreshedRootRows, this.flatRootRows$);

        this.eventDispatcher('rows-updated', [...refreshedRootRows, ...refreshedDraftRootRows, ...refreshedSubrows]);
    }

    private findFirstRequiredColumn(table: Table<HierarchicalRecordRow>, fondId: number) {
        return table.getAllFlatColumns() // todo maybe use get all visible cols
            .filter((column) => column.columnDef.meta.columnType === COLUMN_TYPES.DATA_COLUMN)
            .find((column) => {
                const required = resolveGridFieldAttributeValue(column.columnDef.meta.required, fondId);
                const enabled = resolveGridFieldAttributeValue(column.columnDef.meta.enabled, fondId);
                const editable = resolveGridFieldAttributeValue(column.columnDef.meta.editable, fondId);
                return required && enabled && editable;
            });
    }

    private getOriginalRecordRowSource(recordRow: HierarchicalRecordRow): BehaviorSubject<SourceAwareRecordRow[]> {
        if (isSubrow(recordRow)) {
            return this.flatSubrows$;
        }
        if (isDraft(recordRow)) {
            return this.flatDraftRootRows$;
        }
        return this.flatRootRows$;
    }

    private appendRowTo(recordRow: SourceAwareRecordRow, recordRows$: BehaviorSubject<SourceAwareRecordRow[]>) {
        updateValue(recordRows$, (recordRows) => [...recordRows, recordRow]);
    }

    private removeRowFrom(recordRow: SourceAwareRecordRow, recordRows$: BehaviorSubject<SourceAwareRecordRow[]>) {
        updateValue(recordRows$, (recordRows) => removeAll(recordRows, byIdOf(recordRow)));
    }

    private replaceRowIn(recordRow: SourceAwareRecordRow, recordRows$: BehaviorSubject<SourceAwareRecordRow[]>) {
        updateValue(recordRows$, (recordRows) => replaceAll(recordRows, byIdOf(recordRow), recordRow));
    }

    private replaceRowsIn(replacementRows: SourceAwareRecordRow[], recordRows$: BehaviorSubject<SourceAwareRecordRow[]>) {
        updateValue(recordRows$, (recordRows) => {
            replacementRows.forEach((replacementRow) => {
                recordRows = replaceAll(recordRows, byIdOf(replacementRow), replacementRow);
            });
            return recordRows;
        });
    }

    private flushEditsBuffer() {
        this.bufferFlushSignal$.next();
    }

    private async waitUntilEditsProcessingFinishes() {
        await firstValueFrom(this.isProcessingEdits$.pipe(filter((isProcessingEdits) => !isProcessingEdits), take(1)));
    }
}